import React, { useState, useEffect, useMemo } from 'react';
import { SwordsIcon, SparklesIcon, ZapIcon, TrophyIcon, Loader2Icon, SkullIcon } from 'lucide-react';

const TournamentProgress = ({ isActive }) => {
  const [currentPhase, setCurrentPhase] = useState(0);
  const [subPhase, setSubPhase] = useState(0);
  const [dots, setDots] = useState('');

  // Define the phases of auto-tournament
  const phases = useMemo(() => [
    {
      id: 'round1',
      title: 'Round 1: Initial Battle',
      description: 'All 3 AIs battle with your prompt',
      icon: SwordsIcon,
      color: 'cyan',
      subPhases: [
        'GPT preparing response...',
        'Claude crafting answer...',
        'Gemini generating reply...',
        'Scoring all responses...',
        'Determining first elimination...'
      ]
    },
    {
      id: 'round2',
      title: 'Round 2: Analysis',
      description: '2 survivors critique the eliminated',
      icon: ZapIcon,
      color: 'yellow',
      subPhases: [
        'Analyzing eliminated response...',
        'Survivors critiquing weaknesses...',
        'Generating improved answers...',
        'Scoring critiques...',
        'Determining second elimination...'
      ]
    },
    {
      id: 'round3',
      title: 'Round 3: Championship',
      description: 'Final survivor delivers the definitive answer',
      icon: TrophyIcon,
      color: 'amber',
      subPhases: [
        'Champion reviewing final rival...',
        'Crafting the ultimate response...',
        'Final scoring in progress...',
        'Crowning the winner...'
      ]
    }
  ], []);

  // Animate dots
  useEffect(() => {
    if (!isActive) return;

    const interval = setInterval(() => {
      setDots(prev => prev.length >= 3 ? '' : prev + '.');
    }, 400);

    return () => clearInterval(interval);
  }, [isActive]);

  // Progress through phases automatically based on estimated timing
  useEffect(() => {
    if (!isActive) {
      setCurrentPhase(0);
      setSubPhase(0);
      return;
    }

    // Phase timing estimates (in ms)
    const phaseDurations = [
      10000,  // Round 1: ~10s (3 parallel API calls + scoring)
      12000,  // Round 2: ~12s (2 API calls + analysis)
      10000   // Round 3: ~10s (1 API call + final scoring)
    ];

    const subPhaseDuration = (phaseIndex) => {
      const totalDuration = phaseDurations[phaseIndex];
      const subPhaseCount = phases[phaseIndex].subPhases.length;
      return totalDuration / subPhaseCount;
    };

    let phaseTimer;
    let subPhaseTimer;

    const advanceSubPhase = () => {
      setSubPhase(prev => {
        const maxSubPhases = phases[currentPhase]?.subPhases.length || 1;
        if (prev < maxSubPhases - 1) {
          return prev + 1;
        }
        return prev;
      });
    };

    const advancePhase = () => {
      setCurrentPhase(prev => {
        if (prev < phases.length - 1) {
          setSubPhase(0);
          return prev + 1;
        }
        return prev;
      });
    };

    // Start sub-phase cycling
    subPhaseTimer = setInterval(advanceSubPhase, subPhaseDuration(currentPhase));

    // Schedule phase advance
    phaseTimer = setTimeout(() => {
      advancePhase();
    }, phaseDurations[currentPhase]);

    return () => {
      clearTimeout(phaseTimer);
      clearInterval(subPhaseTimer);
    };
  }, [isActive, currentPhase, phases]);

  if (!isActive) return null;

  const phase = phases[currentPhase];
  const PhaseIcon = phase?.icon || SparklesIcon;

  const colorClasses = {
    cyan: {
      bg: 'bg-cyan-500/10',
      border: 'border-cyan-500/30',
      text: 'text-cyan-300',
      icon: 'text-cyan-400',
      glow: 'shadow-cyan-500/20',
      gradient: 'from-cyan-500'
    },
    yellow: {
      bg: 'bg-yellow-500/10',
      border: 'border-yellow-500/30',
      text: 'text-yellow-300',
      icon: 'text-yellow-400',
      glow: 'shadow-yellow-500/20',
      gradient: 'from-yellow-500'
    },
    amber: {
      bg: 'bg-amber-500/10',
      border: 'border-amber-500/30',
      text: 'text-amber-300',
      icon: 'text-amber-400',
      glow: 'shadow-amber-500/20',
      gradient: 'from-amber-500'
    }
  };

  const colors = colorClasses[phase?.color] || colorClasses.yellow;

  return (
    <div className={`mt-4 p-4 rounded-xl border ${colors.bg} ${colors.border} shadow-lg ${colors.glow} transition-all duration-500`}>
      {/* Progress bar */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-slate-400 font-medium">Tournament Progress</span>
          <span className="text-xs text-slate-400">Round {currentPhase + 1} / {phases.length}</span>
        </div>
        <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-yellow-500 via-orange-500 to-red-500 transition-all duration-500 ease-out"
            style={{ width: `${((currentPhase + (subPhase / (phase?.subPhases.length || 1))) / phases.length) * 100}%` }}
          />
        </div>
      </div>

      {/* Current phase info */}
      <div className="flex items-start gap-4">
        {/* Animated icon */}
        <div className={`relative p-3 rounded-xl ${colors.bg} border ${colors.border}`}>
          <PhaseIcon className={`h-6 w-6 ${colors.icon} animate-pulse`} />
          <div className={`absolute inset-0 rounded-xl ${colors.bg} blur-xl opacity-50`} />
        </div>

        {/* Phase details */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h4 className={`font-bold ${colors.text}`}>{phase?.title}</h4>
            <Loader2Icon className={`h-4 w-4 ${colors.icon} animate-spin`} />
          </div>
          <p className="text-slate-400 text-sm mb-2">{phase?.description}</p>

          {/* Current sub-phase with animated dots */}
          <div className="flex items-center gap-2">
            <span className={`text-sm font-medium ${colors.text}`}>
              {phase?.subPhases[subPhase]}{dots}
            </span>
          </div>
        </div>
      </div>

      {/* Round indicators */}
      <div className="mt-4 flex items-center justify-center gap-3">
        {phases.map((p, i) => {
          const isComplete = currentPhase > i;
          const isCurrent = currentPhase === i;
          const isPending = currentPhase < i;
          const phaseColors = colorClasses[p.color];

          return (
            <div
              key={p.id}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-300 ${
                isComplete
                  ? 'bg-green-500/20 text-green-300 border border-green-500/30'
                  : isCurrent
                  ? `${phaseColors.bg} ${phaseColors.text} border ${phaseColors.border} animate-pulse`
                  : 'bg-slate-800/50 text-slate-500 border border-slate-700/30'
              }`}
            >
              {i === 0 && <SwordsIcon className="h-3 w-3" />}
              {i === 1 && <SkullIcon className="h-3 w-3" />}
              {i === 2 && <TrophyIcon className="h-3 w-3" />}
              <span>R{i + 1}</span>
              {isComplete && <span className="ml-1">✓</span>}
            </div>
          );
        })}
      </div>

      {/* Fun facts / tips while waiting */}
      <div className="mt-4 pt-3 border-t border-slate-700/30">
        <TournamentFact currentPhase={currentPhase} />
      </div>
    </div>
  );
};

// Component to show fun facts about the tournament process
const TournamentFact = ({ currentPhase }) => {
  const facts = [
    "In each round, the lowest-scoring AI gets eliminated from the tournament.",
    "Surviving AIs must critique their eliminated rival and provide an improved answer.",
    "The final champion has proven itself by outlasting 2 competitors through adversarial testing.",
    "Auto-Tournament costs 3 prompts: 1 initial battle + 2 analysis rounds.",
    "Scores are based on relevance, accuracy, depth, clarity, and appropriate verbosity."
  ];

  const [factIndex, setFactIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setFactIndex(prev => (prev + 1) % facts.length);
    }, 7000);
    return () => clearInterval(interval);
  }, [facts.length]);

  return (
    <div className="flex items-start gap-2">
      <SparklesIcon className="h-4 w-4 text-yellow-400 mt-0.5 flex-shrink-0" />
      <p className="text-xs text-slate-400 leading-relaxed">
        <span className="text-yellow-300 font-medium">Did you know? </span>
        {facts[factIndex]}
      </p>
    </div>
  );
};

export default TournamentProgress;
